import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import {
  currentMonthKey,
  downloadAdminSalesCsv,
  fetchAdminSales,
  fetchAdminSalesChart,
} from '../../api/adminSales';

function formatEur(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function monthLabel(monthKey) {
  if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) return monthKey;
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

function chartDayLabel(dateKey) {
  const [, , day] = dateKey.split('-');
  return String(Number(day));
}

function SalesChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover p-3 rounded-lg shadow-elevation-3 border border-border text-sm">
      <p className="font-medium text-foreground mb-1">Jour {label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="text-muted-foreground">
          {entry.name} :{' '}
          <span className="font-semibold text-foreground">
            {entry.name === 'CA TTC'
              ? formatEur(entry.value)
              : entry.value}
          </span>
        </p>
      ))}
    </div>
  );
}

function SalesChartPanel({ month, onMonthChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chart, setChart] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAdminSalesChart(month);
      setChart(res?.data ?? null);
    } catch (e) {
      setError(e?.message || 'Erreur de chargement.');
      setChart(null);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    void load();
  }, [load]);

  const chartData = useMemo(
    () =>
      (chart?.daily ?? []).map((row) => ({
        day: chartDayLabel(row.date),
        caTtcEur: Math.round((row.caTtcEur ?? 0) * 100) / 100,
        count: row.count ?? 0,
      })),
    [chart],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div>
          <label htmlFor="sales-month" className="text-sm font-medium text-foreground block mb-1">
            Mois
          </label>
          <input
            id="sales-month"
            type="month"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={month}
            onChange={(e) => onMonthChange(e.target.value)}
          />
        </div>
        <p className="text-sm text-muted-foreground sm:mb-2 capitalize">
          {monthLabel(month)}
        </p>
      </div>

      {error ? (
        <p className="text-sm text-error" role="alert">{error}</p>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">CA TTC du mois</p>
          <p className="text-2xl font-semibold text-foreground mt-1 tabular-nums">
            {loading ? '…' : formatEur(chart?.totalCaTtcEur ?? 0)}
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Nombre de ventes</p>
          <p className="text-2xl font-semibold text-foreground mt-1 tabular-nums">
            {loading ? '…' : new Intl.NumberFormat('fr-FR').format(chart?.totalCount ?? 0)}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 md:p-6">
        <h3 className="text-base font-semibold text-foreground mb-4">
          CA et ventes par jour
        </h3>
        {loading ? (
          <div className="h-72 bg-muted animate-pulse rounded-lg" />
        ) : (
          <div className="w-full h-72 md:h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="day"
                  stroke="var(--color-text-secondary)"
                  tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="var(--color-text-secondary)"
                  tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                  tickFormatter={(v) => `${v} €`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="var(--color-text-secondary)"
                  tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                  allowDecimals={false}
                />
                <Tooltip content={<SalesChartTooltip />} />
                <Bar
                  yAxisId="left"
                  dataKey="caTtcEur"
                  name="CA TTC"
                  fill="var(--color-primary)"
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="count"
                  name="Ventes"
                  stroke="var(--color-accent)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function SalesTablePanel() {
  const [filters, setFilters] = useState({
    email: '',
    affiliateCode: '',
    dateFrom: '',
    dateTo: '',
  });
  const [applied, setApplied] = useState({
    email: '',
    affiliateCode: '',
    dateFrom: '',
    dateTo: '',
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [sales, setSales] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [summary, setSummary] = useState({ count: 0, totalCaTtcEur: 0 });
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAdminSales({
        ...applied,
        page,
        limit: 50,
      });
      const data = res?.data ?? {};
      setSales(Array.isArray(data.sales) ? data.sales : []);
      setPagination(data.pagination ?? { page: 1, limit: 50, total: 0, totalPages: 1 });
      setSummary(data.summary ?? { count: 0, totalCaTtcEur: 0 });
    } catch (e) {
      setError(e?.message || 'Erreur de chargement.');
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [applied, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = (e) => {
    e?.preventDefault();
    setPage(1);
    setApplied({ ...filters });
  };

  const resetFilters = () => {
    const empty = { email: '', affiliateCode: '', dateFrom: '', dateTo: '' };
    setFilters(empty);
    setApplied(empty);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      await downloadAdminSalesCsv(applied);
    } catch (e) {
      setError(e?.message || 'Export impossible.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <form
        onSubmit={applyFilters}
        className="rounded-lg border border-border bg-card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
      >
        <div>
          <label htmlFor="filter-email" className="text-xs font-medium text-foreground block mb-1">
            E-mail
          </label>
          <input
            id="filter-email"
            type="text"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="client@exemple.fr"
            value={filters.email}
            onChange={(e) => setFilters((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="filter-promo" className="text-xs font-medium text-foreground block mb-1">
            Code promo
          </label>
          <input
            id="filter-promo"
            type="text"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
            placeholder="PROMO2026"
            value={filters.affiliateCode}
            onChange={(e) => setFilters((f) => ({ ...f, affiliateCode: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="filter-from" className="text-xs font-medium text-foreground block mb-1">
            Date début
          </label>
          <input
            id="filter-from"
            type="date"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={filters.dateFrom}
            onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="filter-to" className="text-xs font-medium text-foreground block mb-1">
            Date fin
          </label>
          <input
            id="filter-to"
            type="date"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={filters.dateTo}
            onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2 lg:col-span-4 flex flex-wrap gap-2">
          <Button type="submit" size="sm">Rechercher</Button>
          <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
            Réinitialiser
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName="Download"
            disabled={exporting || loading}
            onClick={() => void handleExport()}
          >
            {exporting ? 'Export…' : 'Exporter CSV'}
          </Button>
        </div>
      </form>

      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        <span>
          {summary.count} vente{summary.count !== 1 ? 's' : ''} · CA total{' '}
          <span className="font-medium text-foreground">{formatEur(summary.totalCaTtcEur)}</span>
        </span>
      </div>

      {error ? (
        <p className="text-sm text-error" role="alert">{error}</p>
      ) : null}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">E-mail</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Plateforme</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Produit</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Montant TTC</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Code promo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Chargement…
                  </td>
                </tr>
              ) : sales.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Aucune vente trouvée.
                  </td>
                </tr>
              ) : (
                sales.map((sale) => (
                  <tr key={sale.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums">
                      {formatDateTime(sale.paidAt)}
                    </td>
                    <td className="px-4 py-3">{sale.email}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          sale.source === 'hubup'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-secondary/15 text-secondary-foreground'
                        }`}
                      >
                        {sale.source === 'hubup' ? 'HubUP' : 'CRM'}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-xs">{sale.productLabel}</td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">
                      {formatEur(sale.amountTtcEur)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {sale.affiliateCode || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Page {pagination.page} / {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Précédent
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function SalesAdmin() {
  const [tab, setTab] = useState('chart');
  const [month, setMonth] = useState(currentMonthKey());

  return (
    <>
      <Helmet>
        <title>Ventes - Administration CRM</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        <main className="main-content">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-1">
              Ventes
            </h1>
            <p className="text-sm text-muted-foreground">
              Suivi des achats HubUP et CRM (packs notifications, e-mails, accès à vie).
            </p>
          </div>

          <div className="flex gap-1 p-1 rounded-lg bg-muted w-fit mb-6">
            <button
              type="button"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth flex items-center gap-2 ${
                tab === 'chart'
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setTab('chart')}
            >
              <Icon name="BarChart3" size={16} />
              Graphique
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth flex items-center gap-2 ${
                tab === 'table'
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setTab('table')}
            >
              <Icon name="Table" size={16} />
              Tableau
            </button>
          </div>

          {tab === 'chart' ? (
            <SalesChartPanel month={month} onMonthChange={setMonth} />
          ) : (
            <SalesTablePanel />
          )}
        </main>
      </div>
    </>
  );
}
